import api from "./api";

export type McRunStatus =
  | "requested"
  | "provisioning"
  | "running"
  | "finalizing"
  | "completed"
  | "failed"
  | "cancelled";

export type McRunOutcome =
  "passed" | "issues_found" | "inconclusive" | "not_tested";

export type McRunTrigger = "manual" | "deploy" | "pr" | "schedule";

/** new: not triaged yet. regressed: seen again after it was marked fixed. */
export type McIssueStatus =
  "new" | "confirmed" | "fixed" | "regressed" | "false_positive" | "wont_fix";

/** The issue a finding was linked to: one problem across runs. */
export interface McFindingIssue {
  id: number;
  title: string;
  status: McIssueStatus;
  /** This run is where the issue was first seen */
  firstSeenHere: boolean;
}

export interface McFinding {
  id: string;
  title: string;
  severity?: string;
  kind?: string;
  /** Unset on runs from before issues, and while a run is still active */
  issue?: McFindingIssue;
}

export interface McIssueSighting {
  runId: string;
  findingId: string;
  at: string;
}

export interface McIssue {
  id: number;
  target: string;
  title: string;
  severity?: string;
  kind?: string;
  status: McIssueStatus;
  note?: string;
  seenCount: number;
  firstSeen?: McIssueSighting;
  lastSeen?: McIssueSighting;
  createdAt: string;
  updatedBy?: string;
  updatedAt: string;
}

export interface McIssueOccurrence extends McIssueSighting {
  title: string;
  severity?: string;
  kind?: string;
}

/** One entry in a target's memory, which every run is given. */
export interface McMemoryEntry {
  id: string;
  target: string;
  text: string;
  why?: string;
  sourceRunId?: string;
  sourceMissionId?: string;
  /** "mc" when a run added it */
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt: string;
}

/** Who memory and issue changes made by runs are attributed to. */
export const MC_AGENT = "mc";

/** A finding as recorded in its `finding` event, with the detail the run summary leaves out. */
export interface McFindingDetail extends McFinding {
  missionId?: string;
  description?: string;
  repro?: string;
  expected?: string;
  actual?: string;
  recordedAt?: string;
  artifactKey?: string;
  evidenceArtifactIds?: string[];
  [field: string]: unknown;
}

/** The playbook id of a run without one: its instructions are the whole task. */
export const MC_CUSTOM_PLAYBOOK = "custom";

export interface McPlaybook {
  id: string;
  title: string;
  /** What the playbook is for and how far to go. Runs read it. */
  description?: string;
  /** Runs use the default unless they name another */
  default: boolean;
}

export interface McTarget {
  id: string;
  name: string;
  /** The default first */
  playbooks: McPlaybook[];
}

/** One thing a run checks. mc-api assigns the id. */
export interface McPlaybookCheck {
  id: string;
  text: string;
  notes?: string;
}

export interface McPlaybookDetail extends McPlaybook {
  checks: McPlaybookCheck[];
  revision: number;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt: string;
}

export interface McPlaybookRevision {
  revision: number;
  title: string;
  description?: string;
  checks: McPlaybookCheck[];
  createdBy?: string;
  createdAt: string;
}

export interface McPlaybookFields {
  title: string;
  description?: string;
  /** Checks without an id are new */
  checks: (Omit<McPlaybookCheck, "id"> & { id?: string })[];
}

export interface McUsageRow {
  key: string;
  count: number;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  seconds: number;
}

export interface McUsage {
  totalUsd: number;
  byKind: McUsageRow[];
  byMission: McUsageRow[];
  byModel: McUsageRow[];
}

export interface McArtifact {
  key: string;
  id?: string;
  mime: string;
  bytes: number;
  sha256: string;
  missionId?: string;
}

/** Where a run came from (e.g. the deploy workflow run and the PR it shipped); for people, not the agent. */
export interface McRunSource {
  url?: string;
  /** owner/name on GitHub */
  repo?: string;
  pr?: number;
  prTitle?: string;
}

export interface McRun {
  id: string;
  target: string;
  trigger: McRunTrigger;
  input: {
    /** Playbook id, or "custom"; unset on older runs */
    playbook?: string;
    /** The playbook's title when the run was requested */
    playbookTitle?: string;
    /** The playbook version the run used; unset on older runs */
    playbookRevision?: number;
    playbookDescription?: string;
    playbookChecks?: McPlaybookCheck[];
    /** Runs requested before the purpose became the description */
    playbookPurpose?: string;
    /** Runs requested before playbooks had checks: the playbook's Markdown */
    playbookBody?: string;
    /** The run's instructions */
    guidanceText?: string;
    /** Older runs only: what they were asked to do, and the preset they came from */
    goal?: string;
    preset?: string;
    pr?: number;
    shaRange?: string;
    budgetUsd?: number;
    maxMinutes?: number;
    maxBrowsers?: number;
  };
  sha?: string;
  previousSha?: string;
  source?: McRunSource;
  requestedBy?: string;
  status: McRunStatus;
  outcome?: McRunOutcome | string;
  summary?: string;
  error?: string;
  findings: McFinding[];
  costUsd: number;
  createdAt: string;
  startedAt?: string;
  heartbeatAt?: string;
  finishedAt?: string;
  cancelRequestedAt?: string;
  cancelRequestedBy?: string;
  url?: string;
}

export interface McRunDetail extends McRun {
  artifacts: McArtifact[];
}

export interface McEvent {
  seq: number;
  at: string;
  type: string;
  missionId?: string;
  summary: string;
  payload?: unknown;
}

/** A target's run settings. Until someone saves them they're the defaults, with no updatedAt. */
export interface McSettings {
  budgetUsd: number;
  maxMinutes: number;
  maxBrowsers: number;
  updatedBy?: string;
  updatedAt?: string;
}

export type McSettingsFields = Pick<
  McSettings,
  "budgetUsd" | "maxMinutes" | "maxBrowsers"
>;

export const MC_MAX_BROWSERS = 4;

export interface StartMcRunPayload {
  /** A playbook id, or "custom" (then guidanceText is required) */
  playbook: string;
  guidanceText?: string;
}

export const MC_DEFAULT_TARGET = "knowtex-gov";

export const MC_ACTIVE_STATUSES: McRunStatus[] = [
  "requested",
  "provisioning",
  "running",
  "finalizing",
];

export const isActiveRun = (run?: Pick<McRun, "status"> | null): boolean =>
  !!run && MC_ACTIVE_STATUSES.includes(run.status);

export const MC_EVENTS_PAGE_SIZE = 1000;

/** The runs table asks for this many. The server caps the list at the same number. */
export const MC_RUNS_LIMIT = 200;

const runPath = (runId: string) => `/mc/runs/${encodeURIComponent(runId)}`;
const playbooksPath = (targetId: string) =>
  `/mc/targets/${encodeURIComponent(targetId)}/playbooks`;
const settingsPath = (targetId: string) =>
  `/mc/targets/${encodeURIComponent(targetId)}/settings`;

/** Every event after `afterSeq`, following pages until a short one. */
async function getRunEventsAfter(
  runId: string,
  afterSeq: number,
): Promise<McEvent[]> {
  const events: McEvent[] = [];
  let after = afterSeq;
  for (;;) {
    const { events: page = [] } = (await api.get(
      `${runPath(runId)}/events?after=${after}&limit=${MC_EVENTS_PAGE_SIZE}`,
    )) as { events?: McEvent[] };
    const next = page.at(-1)?.seq ?? after;
    if (!page.length || next <= after) return events;
    events.push(...page);
    if (page.length < MC_EVENTS_PAGE_SIZE) return events;
    after = next;
  }
}

const mcApi = {
  getTargets: () => api.get("/mc/targets") as Promise<{ targets: McTarget[] }>,

  getPlaybooks: (targetId: string) =>
    api.get(playbooksPath(targetId)) as Promise<{
      playbooks: McPlaybookDetail[];
    }>,

  createPlaybook: (targetId: string, fields: McPlaybookFields) =>
    api.post(playbooksPath(targetId), fields) as Promise<{
      playbook: McPlaybookDetail;
    }>,

  updatePlaybook: (
    targetId: string,
    playbookId: string,
    patch: Partial<McPlaybookFields> & { default?: true },
  ) =>
    api.patch(
      `${playbooksPath(targetId)}/${encodeURIComponent(playbookId)}`,
      patch,
    ) as Promise<{
      playbook: McPlaybookDetail;
    }>,

  deletePlaybook: (targetId: string, playbookId: string) =>
    api.delete(
      `${playbooksPath(targetId)}/${encodeURIComponent(playbookId)}`,
    ) as Promise<{ ok: boolean }>,

  getPlaybookRevisions: (targetId: string, playbookId: string) =>
    api.get(
      `${playbooksPath(targetId)}/${encodeURIComponent(playbookId)}/revisions`,
    ) as Promise<{ revisions: McPlaybookRevision[] }>,

  getSettings: (targetId: string) =>
    api.get(settingsPath(targetId)) as Promise<{ settings: McSettings }>,

  updateSettings: (targetId: string, fields: Partial<McSettingsFields>) =>
    api.patch(settingsPath(targetId), fields) as Promise<{
      settings: McSettings;
    }>,

  getRuns: ({ limit = MC_RUNS_LIMIT }: { limit?: number } = {}) =>
    api.get(`/mc/runs?limit=${limit}`) as Promise<{ runs: McRun[] }>,

  getRun: (runId: string) => api.get(runPath(runId)) as Promise<McRunDetail>,

  getRunEventsAfter,

  getRunUsage: (runId: string) =>
    api.get(`${runPath(runId)}/usage`) as Promise<McUsage>,

  getRunBundle: (runId: string) =>
    api.blob(`${runPath(runId)}/bundle.zip`) as Promise<{
      blob: Blob;
      filename?: string;
    }>,

  getArtifactUrl: (runId: string, key: string) =>
    api.get(
      `${runPath(runId)}/artifact-url?key=${encodeURIComponent(key)}`,
    ) as Promise<{ url: string }>,

  startRun: (payload: StartMcRunPayload) =>
    api.post("/mc/runs", payload) as Promise<McRun>,

  cancelRun: (runId: string) =>
    api.post(`${runPath(runId)}/cancel`) as Promise<McRun>,

  getMemory: () =>
    api.get("/mc/memory") as Promise<{ entries: McMemoryEntry[] }>,

  createMemory: (entry: { text: string; why?: string }) =>
    api.post("/mc/memory", entry) as Promise<{ entry: McMemoryEntry }>,

  updateMemory: (entryId: string, patch: { text?: string; why?: string }) =>
    api.patch(`/mc/memory/${encodeURIComponent(entryId)}`, patch) as Promise<{
      entry: McMemoryEntry;
    }>,

  deleteMemory: (entryId: string) =>
    api.delete(`/mc/memory/${encodeURIComponent(entryId)}`) as Promise<{
      ok: boolean;
    }>,

  getIssues: () => api.get("/mc/issues") as Promise<{ issues: McIssue[] }>,

  getIssue: (issueId: number) =>
    api.get(`/mc/issues/${issueId}`) as Promise<{
      issue: McIssue;
      occurrences: McIssueOccurrence[];
    }>,

  updateIssue: (
    issueId: number,
    patch: { status?: McIssueStatus; note?: string },
  ) => api.patch(`/mc/issues/${issueId}`, patch) as Promise<{ issue: McIssue }>,
};

export default mcApi;
